import {
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { labelForTransferReason } from 'common/utils/transferOutcomes';
import PropTypes from 'prop-types';

export const NA = '—';

const RESULT_COLUMNS = ['Order Id', 'Status', 'Reason'];

export const transferResultRowShape = PropTypes.shape({
  orderId: PropTypes.string.isRequired,
  isTransferred: PropTypes.bool,
  reason: PropTypes.string,
  resultingOrderId: PropTypes.string,
});

// Matches the app's standard status chip convention (CentralFillQueueStatusChip,
// SendToCentralFillResults): a plain MUI Chip using the `color` prop, not custom pills.
const TransferStatusChip = ({ isTransferred }) => (
  <Chip
    size='small'
    variant='outlined'
    label={isTransferred ? 'Transferred' : 'Not transferred'}
    color={isTransferred ? 'success' : 'error'}
    sx={{ fontWeight: 600 }}
  />
);

TransferStatusChip.propTypes = { isTransferred: PropTypes.bool };
TransferStatusChip.defaultProps = { isTransferred: false };

/**
 * One row per requested ORDER — the transfer verdict is order-level, so rows are keyed by orderId
 * and never by line item. Rows are expected pre-sorted by the caller (not-transferred first, via
 * buildTransferResultRows). Reason shows only for blocked rows.
 *
 * Rows still carry `resultingOrderId` (the new order id when a partial selection split an order),
 * but it is deliberately NOT displayed: splits are the uncommon case, so the column rendered an
 * em dash on almost every row. Surfacing it again is a display-only change.
 */
const TransferResultsTable = ({ rows, maxHeight }) => (
  <TableContainer
    sx={{
      maxHeight,
      border: 1,
      borderColor: 'divider',
      borderRadius: 2,
      '&::-webkit-scrollbar': { width: 10, height: 10 },
      '&::-webkit-scrollbar-thumb': {
        backgroundColor: '#cfd4dc',
        borderRadius: 999,
        border: '3px solid #fff',
      },
    }}
  >
    <Table
      size='small'
      stickyHeader
      aria-label='Transfer results table'
      sx={{
        '& td, & th': { py: 0.75 },
        '& tbody td': { borderColor: '#f0f2f6', fontVariantNumeric: 'tabular-nums' },
        '& tbody tr:last-child td': { border: 0 },
      }}
    >
      <TableHead>
        <TableRow
          sx={{
            '& th': {
              fontWeight: 700,
              fontSize: 11,
              letterSpacing: '0.04em',
              textTransform: 'uppercase',
              color: 'text.secondary',
              bgcolor: '#f8f9fb',
            },
          }}
        >
          {RESULT_COLUMNS.map((column) => (
            <TableCell key={column} sx={{ whiteSpace: 'nowrap' }}>
              {column}
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map(({ orderId, isTransferred, reason }) => (
          <TableRow key={orderId} hover>
            <TableCell sx={{ whiteSpace: 'nowrap' }}>{orderId}</TableCell>
            <TableCell sx={{ whiteSpace: 'nowrap' }}>
              <TransferStatusChip isTransferred={isTransferred} />
            </TableCell>
            <TableCell sx={{ color: isTransferred ? 'text.disabled' : 'error.main' }}>
              {isTransferred ? NA : labelForTransferReason(reason) || 'Not transferred'}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </TableContainer>
);

TransferResultsTable.propTypes = {
  rows: PropTypes.arrayOf(transferResultRowShape).isRequired,
  maxHeight: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};
TransferResultsTable.defaultProps = { maxHeight: 360 };

export default TransferResultsTable;
